import React, { useEffect } from 'react';

import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { onBoardCompleted } from '@/modules/onboarding/helper';
import useOnboardingRoute from '@/modules/onboarding/hooks/useOnboardingRoute';
import { UserStatusEnum } from '@/types/schema';

import { useAppConfig } from '../../hooks/useAppConfig';
import authRoutes from '../../pages/auth/routes';
import routes from '../../pages/corporate/routes';

const OnBoardingFlow: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigation = useNavigation();
  const user = useUserContext();
  const { [FeatureFlags.EnableOnboarding]: showOnBoarding } = useFeatureFlags();
  const { site } = useAppConfig();
  const {
    onboardingRoute,
    verifyEmailRoute,
    isNewOnboarding,
    isCrossAppOnboarding,
    canEnterOnboarding,
    markHandoff,
  } = useOnboardingRoute();

  //searchParam return string or null
  const shouldBoarding = showOnBoarding !== false;
  const emailNotVerified = user?.status === UserStatusEnum.EmailNotVerified;
  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname : navigation.asPath.split('?')[0];
  const isVerificationOrOnboardingRoute =
    currentPath === authRoutes.VerifyEmail() ||
    currentPath === authRoutes.VerifyEmailV2() ||
    currentPath === authRoutes.VerifyYourself() ||
    currentPath === authRoutes.AboutYourself();

  const willRedirectToVerifyEmail =
    emailNotVerified && Boolean(user.id) && !isVerificationOrOnboardingRoute;
  // `currentPath !== onboardingRoute` cannot guard a cross-app destination — the academy app
  // never renders at a `/w` path — so the session hop guard stands in for it there.
  const willRedirectToOnboarding =
    shouldBoarding &&
    !emailNotVerified &&
    Boolean(user.id) &&
    !onBoardCompleted(user) &&
    !isVerificationOrOnboardingRoute &&
    (isCrossAppOnboarding ? canEnterOnboarding : currentPath !== onboardingRoute);

  useEffect(() => {
    if (site.academySetupCompleted === false && navigation.pathname !== routes.AcademySetup()) {
      // setting onboarding info is async process and after onboarding , redireciton causing infininte redirection , hence loading full page
      window.location.replace(`${window.location.origin}${routes.AcademySetup()}`);
      // navigation.replace(routes.AcademySetup());
    }

    if (willRedirectToVerifyEmail) {
      navigation.push(verifyEmailRoute, { setFrom: true, appendQuery: false });
    }

    if (willRedirectToOnboarding) {
      markHandoff();
      navigation.push(onboardingRoute, { setFrom: true, appendQuery: false });
    }
  }, [
    markHandoff,
    navigation,
    onboardingRoute,
    site.academySetupCompleted,
    verifyEmailRoute,
    willRedirectToOnboarding,
    willRedirectToVerifyEmail,
  ]);

  // For the AI-onboarding and workeffy flows, suppress the flash of home/app content that
  // would otherwise appear before the redirect fires. The workeffy hop is a full document
  // load, so the window it covers is longer than a Next route change.
  if ((site.isUserOnboardingAiEnabled || isNewOnboarding) && user.id) {
    if (willRedirectToVerifyEmail || willRedirectToOnboarding) {
      return null;
    }
  }

  return <>{children}</>;
};

export default OnBoardingFlow;
